import { useCallback, useMemo, useState } from 'react'

import { useSearchParams } from 'react-router'

import { MAX_SEARCH } from '../../series.constants'
import type { SeriesListFilter } from '../../api/series.types'
import { SEARCH_STORAGE_KEY, parseListParams, writeListParams } from './seriesListParams'

const readSearch = (): string => {
  try {
    return (sessionStorage.getItem(SEARCH_STORAGE_KEY) ?? '').slice(0, MAX_SEARCH)
  } catch {
    return ''
  }
}

const writeSearch = (value: string) => {
  try {
    if (value) sessionStorage.setItem(SEARCH_STORAGE_KEY, value)
    else sessionStorage.removeItem(SEARCH_STORAGE_KEY)
  } catch {
    // storage unavailable: the search lives in memory only
  }
}

const withListParams = (prev: URLSearchParams, filter: Omit<SeriesListFilter, 'search'>) => {
  const next = new URLSearchParams(prev)
  ;['status', 'doctor', 'page'].forEach((key) => next.delete(key))
  Object.entries(writeListParams(filter)).forEach(([key, value]) => next.set(key, value))
  return next
}

export const useSeriesListFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [search, setSearch] = useState(readSearch)
  const urlPart = useMemo(() => parseListParams(searchParams), [searchParams])
  const filter: SeriesListFilter = useMemo(() => ({ ...urlPart, search }), [urlPart, search])

  const setFilter = useCallback(
    (patch: Partial<SeriesListFilter>) => {
      if (patch.search !== undefined) {
        writeSearch(patch.search)
        setSearch(patch.search)
      }
      const { search: _search, ...rest } = patch
      setSearchParams(
        (prev) => {
          const current = parseListParams(prev)
          return withListParams(prev, { ...current, page: 1, ...rest })
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const reset = useCallback(() => {
    writeSearch('')
    setSearch('')
    setSearchParams((prev) => withListParams(prev, { status: 'active', doctorId: null, page: 1 }), {
      replace: true,
    })
  }, [setSearchParams])

  return { filter, setFilter, reset }
}
